import type { Translations } from "./types.ts";

export const en: Translations = {
  nav: {
    basketball: "Basketball",
    tennis: "Tennis",
    soccer: "Soccer",
    golf: "Golf",
    classic: "Classic",
    ai: "AI",
    comingSoon: "Coming Soon",
    aiTeams: "AI Teams",
  },
  home: {
    heroTitle: "Elevate Your Shot.",
    heroSubtitle:
      "Track, analyze, and perfect every basketball shot. Voice-controlled counting, 19-zone court analytics, and Apple Watch integration.",
    chooseEdge: "Choose Your Edge.",
    classicBadge: "Classic",
    aiBadge: "AI",
    classicTitle: "ShootCreator",
    aiTitle: "ShootCreator AI",
    classicDesc:
      "The essential shot counter for every basketball player. Track your practice with voice control and detailed zone analytics.",
    aiDesc:
      "Your personal AI shooting coach. Get real-time error analysis, biometric insights, and voice-powered practice notes.",
    classicFeatures: [
      "Voice-controlled shot counting",
      "19-zone NBA-style court analytics",
      "Apple Watch integration",
      "Session history & tags",
      "100% offline, privacy-first",
    ],
    aiFeatures: [
      "Everything in Classic, plus:",
      "AI error classification (Arc, Depth, Direction)",
      "Heart rate monitoring & zones",
      "Voice memo transcription",
      "Biometric session timeline",
    ],
    free: "Free",
    aiPrice: "$10/month",
    learnMore: "Learn More",
    courtAnalytics: "Court Analytics",
    zonesStat: "Court Zones",
    devicesStat: "Devices",
    offlineStat: "Offline",
  },
  classic: {
    heroWordmark: "ShootCreator\nClassic",
    heroSubtitle: "Your Personal Shot Counter.",
    voiceTitle: "Hands-Free. Voice-Powered.",
    voiceDesc:
      "Say 'in' or 'out' to log shots without touching your phone. The app listens and counts, so you can keep your hands on the ball.",
    voiceListening: "Listening...",
    watchTitle: "Dual Device. Zero Friction.",
    watchDesc:
      "Track shots from your wrist. Select zones, log makes and misses, all without reaching for your phone.",
    courtTitle: "19 Zones. Complete Picture.",
    courtDesc:
      "NBA-style shot chart with 19 zones covering restricted area, paint, mid-range, three-point, and deep range.",
    courtRecentSessions: "Recent Sessions",
    courtFgLabel: "FG",
    courtGroupRestricted: "Restricted",
    courtGroupPaint: "Paint",
    courtGroupMidRange: "Mid-Range",
    courtGroup3pt: "3PT",
    courtGroupDeep: "Deep",
    sessionTitle: "Every Session. Every Detail.",
    sessionDesc: "Complete session history with tags, zone breakdowns, and field goal percentages.",
    sessionFgLabel: "Field Goal %",
    privacyTitle: "Your Data Stays Yours.",
    privacyDesc: "All data is stored locally on your device. No accounts, no cloud, no tracking.",
    privacyFeatures: [
      { value: "None", label: "Internet Required" },
      { value: "Zero", label: "Cloud Storage" },
      { value: "Never", label: "Data Shared" },
    ],
    ctaTitle: "Free to Start.",
    ctaButton: "Download Free",
    ctaNote: "No account required. No in-app purchases.",
  },
  ai: {
    heroWordmark: "ShootCreator\nAI",
    heroSubtitle: "Your AI Shot Coach.",
    everythingTitle: "Everything in Classic.",
    everythingPlus: "Plus...",
    everythingFeatures: [
      { icon: "🎙️", title: "Voice Control", desc: "Hands-free shot counting" },
      { icon: "🏀", title: "19 Zones", desc: "NBA-style court analytics" },
      { icon: "⌚", title: "Apple Watch", desc: "Wrist-based tracking" },
      { icon: "📊", title: "Session History", desc: "Complete practice logs" },
      { icon: "📴", title: "Offline", desc: "No internet required" },
      { icon: "🔒", title: "Privacy", desc: "Local data storage" },
    ],
    errorTitle: "Shot Error Meter",
    errorArc: {
      title: "ARC",
      desc: "Flat arc surges to 83% on mid-range jumpers above HR 160. Leg fatigue drops release height. Focus on follow-through and wrist snap under fatigue.",
    },
    errorDepth: {
      title: "DEPTH",
      desc: "Short misses spike from 41% to 78% on deep 3PT above HR 155. Lower body push weakens under fatigue — focus on leg drive and release timing.",
    },
    errorDirection: {
      title: "DIRECTION",
      desc: "Left misses surge to 72% on left-wing 3PT above HR 170. Hip rotation breaks down under fatigue — drill lateral balance.",
    },
    heartTitle: "Your Heart Tells the Story.",
    heartDesc:
      "Track heart rate zones throughout your session. See how fatigue affects your shooting accuracy.",
    heartBpm: "BPM",
    voiceTitle: "Every Word. Every Insight.",
    voiceStatusRecording: "Session Recording",
    voiceStatusAnalyzing: "Analyzing...",
    voiceStatusReport: "Session Report",
    voiceSessionLines: [
      {
        speaker: "You",
        text: "Corner three, made.",
        tags: [
          { label: "3PT", type: "zone" },
          { label: "Made", type: "result" },
        ],
      },
      {
        speaker: "You",
        text: "Elbow jumper, missed short.",
        tags: [
          { label: "Mid", type: "zone" },
          { label: "Miss", type: "result" },
          { label: "Short", type: "error" },
        ],
      },
      {
        speaker: "You",
        text: "Legs feeling heavy. Release was flat.",
        tags: [{ label: "Flat Arc", type: "error" }],
      },
      {
        speaker: "You",
        text: "Free throw, swish.",
        tags: [
          { label: "Paint", type: "zone" },
          { label: "Made", type: "result" },
        ],
      },
    ],
    voiceReportIntro:
      "Starting from heart rate impact, I analyzed your weak zones and shot tendencies. Here's your personalized drill plan.",
    voiceInsightCards: [
      {
        icon: "\uD83D\uDD0D",
        title: "HR Impact \u00D7 Zone Analysis",
        value: "Mid-jumper FG% drops 38%\u219222% above HR 155. Worst at elbow pull-ups.",
        change: "-16%",
        changeType: "negative",
        chartType: "zoneFg",
      },
      {
        icon: "\uD83C\uDFAF",
        title: "AI Drill Plan",
        value: "5 drills targeting your HR 155+ weakness zones.",
        change: "+15%",
        changeType: "positive",
        chartType: "drillPlan",
      },
      {
        icon: "\uD83D\uDCCA",
        title: "3D Error Meter",
        value: "Short/Long/Flat/Left/Right surge points identified across HR zones.",
        change: "62%",
        changeType: "negative",
        chartType: "arcPattern",
      },
    ],
    voiceDrillSteps: [
      {
        id: "zone-green",
        time: "HR < 140",
        label: "Mid-range catch & shoot ×20",
        detail: "Build muscle memory at optimal HR. FG% 65%+ in this zone",
        color: "#34c759",
        context: "Baseline: establish form before fatigue sets in",
      },
      {
        id: "zone-yellow",
        time: "HR 140-155",
        label: "3PT spot-up ×15 (30s rest between sets)",
        detail: "FG% starts declining. Focus: arc consistency & follow-through",
        color: "#ffcc00",
        context: "Arc tendency: flat arcs begin here. Conscious high release",
      },
      {
        id: "zone-orange",
        time: "HR 155-165",
        label: "Left elbow pull-up jumper ×12",
        detail: "Weakest spot under fatigue. Current 34% FG → Target 45%+",
        color: "#ff9f0a",
        context: "← Mid-jumper weakness zone. Focus on arc under fatigue",
      },
      {
        id: "zone-red",
        time: "HR 165-180",
        label: "Sprint → catch & shoot ×8 (game-speed)",
        detail: "Clutch simulation. FG% drops 23% here — build resilience",
        color: "#ff3b30",
        context: "← Zone 5 FG% drop. Build fatigue tolerance to improve",
      },
      {
        id: "cooldown",
        time: "HR < 130",
        label: "Free throw routine ×15 (cooldown)",
        detail: "Flush lactate, regain rhythm. FG% recovers +18% below 140",
        color: "#34c759",
        context: "Recovery: restore shooting rhythm after high-HR drills",
      },
    ],
    transcriptLines: [
      { speaker: "You", text: "Mid-range from the left elbow, felt good on release." },
      {
        speaker: "AI",
        text: "Shot logged: Made. Your mid-range FG% is 62% today — 8% above average.",
      },
      { speaker: "You", text: "That one was short. Legs are getting heavy." },
      {
        speaker: "AI",
        text: "Tagged as short miss. Your HR is at 165 BPM — FG% dropped 12% in the last 5 minutes.",
      },
      { speaker: "You", text: "Should I take a break?" },
      {
        speaker: "AI",
        text: "Recommended: 3-min rest. Past sessions show accuracy recovers 15% after cooldown.",
      },
    ],
    dashboardTitle: "The Complete Picture.",
    dashboardDesc: "Shots, heart rate, and voice memos — all on one timeline.",
    modelTitle: "Your AI. Your Choice.",
    modelSubtitle: "Unlock the power of your real data with any LLM.",
    modelTagline:
      "web \u00B7 mobile \u00B7 watch \u2014 the complete sports improvement experience, powered by the AI you trust.",
    modelYourModel: "Your Model",
    modelLocalLlm: "Local LLM",
    modelAnyCompatible: "Any OpenAI-compatible",
    planByok: "BYOK",
    planSubscribe: "Subscribe",
    planFreePrice: "Free",
    planFreeNote: "Use your own API key",
    planFreeFeatures: [
      "All AI analysis features",
      "Choose any LLM provider",
      "Use local models (Qwen, Ollama)",
      "Full data control",
      "Community support",
    ],
    planFreeCta: "Get Started",
    planSubPrice: "$10",
    planSubNote: "/month",
    planSubFeatures: [
      "All AI analysis features",
      "No API key setup needed",
      "Always optimized model",
      "Automatic updates",
      "Priority support",
    ],
    planSubCta: "Start Free Trial",
    analyticsTitle: "Data-Driven Improvement.",
    analyticsSubtitle:
      "Heart rate, shot accuracy, and zone analysis — correlated in real time to reveal what matters most.",
    analyticsTimelineLabel: "HR + Shot Timeline",
    analyticsBpmThreshold: "160 BPM",
    analyticsZoneTitle: "Zone FG% Breakdown",
    analyticsZoneGroups: ["Zone 1-2 (Paint)", "Zone 3 (Mid)", "Zone 4 (3PT)", "Zone 5 (Deep)"],
    analyticsMonthlyTitle: "3-Month Progression",
    analyticsMonths: ["Jan", "Feb", "Mar"],
    analyticsSessions: "sessions",
    analyticsMonthlyInsight:
      "Zone 3 FG improved 16% after AI-guided focus drills across 45 sessions.",
    analyticsInsights: [
      "Your FG% drops 23% when HR exceeds 160 BPM. Consider 3-min rests.",
      "Zone 3 accuracy improved 16% over 3 months with focused mid-range drills.",
      "You shoot 8% better from left elbow after 10-min warmup.",
      "March sessions show best accuracy in first 20 minutes.",
    ],
    dashboardShots: "Shots",
    dashboardShotsUnit: "Shots",
    dashboardSessionDuration: "60 min session",
    dashboardHeartRate: "Heart Rate",
    dashboardVoiceMemos: "Voice Memos",
    dashboardVoiceLines: [
      '"Corner three, made"',
      '"Elbow, missed short"',
      '"Free throw, swish"',
      '"Wing three, missed left"',
      '"Top of key, made"',
      '"Right wing three, short"',
      '"Elbow jumper, too flat"',
      '"Paint finish, and one"',
    ],
    dashboardInsights: [
      { label: "Direction", value: "275 of 500 shots balanced L/R — 55% consistency" },
      { label: "Arc", value: "200 of 500 shots optimal arc — 40% consistency" },
      { label: "Depth", value: "220 of 500 shots accurate depth — 44% consistency" },
      { label: "HR Zone Impact", value: "FG% by heart rate zone across 500 shots" },
      { label: "Area Performance", value: "FG% and avg HR across 500 shots by area" },
    ],
    gaugeLabels: {
      direction: { left: "L", right: "R" },
      arc: { left: "Flat", right: "High" },
      depth: { left: "Short", right: "Over" },
    },
    progressionTitle: "Sessions Stack Up.",
    progressionDesc:
      "One session becomes a month, then three, then six — and the improvement becomes undeniable.",
    progressionSessions: "sessions",
    progressionShots: "shots",
    progressionFg: "FG%",
    progressionImprovement: "Improvement",
    progressionPeriods: [
      { label: "1 Month", sessions: 12, shots: "6,000", fg: 54, dir: 55, arc: 40, depth: 44 },
      { label: "3 Months", sessions: 38, shots: "19,000", fg: 58, dir: 61, arc: 48, depth: 52 },
      { label: "6 Months", sessions: 82, shots: "41,000", fg: 63, dir: 68, arc: 57, depth: 61 },
    ],
    ctaTitle: "Start Your Free Trial.",
    ctaSubtitle: "$10/month after trial.",
    ctaButton: "Get Started",
    ctaNote: "Cancel anytime. No commitment.",
    // HR Zone Matrix
    hrZoneTitle: "HR \u00D7 Zone Analysis",
    hrZoneDesc: "FG% by heart rate zone and court area",
    hrZoneWeakness: "WEAKNESS",
    hrZoneWeapon: "WEAPON",
    hrZoneWeaknessDesc:
      "Mid-range FG% drops 68% in Zone 4-5. Flat arc tendency under fatigue. Recommend form-maintenance drills at elevated HR.",
    hrZoneWeaponDesc: "Paint FG% stays 72% even above HR 175. Keep sharpening your dominant area.",
    hrZoneZones: ["Paint", "Mid", "3PT", "Deep"],
    hrZoneHrBands: ["Z1 <130", "Z2 130-145", "Z3 145-160", "Z4 160-175", "Z5 175+"],
    hrZoneSuggestTitle: "AI Drill Suggestion",
    hrZoneBenchmarkYou: "Your Stats",
    hrZoneBenchmarkRec: "vs Rec League",
    hrZoneBenchmarkNba: "vs NBA Avg",
    hrZoneBenchmarkNcaa: "vs NCAA Top 100",
    hrZoneBenchmarkCurry: "vs Steph Curry",
    hrZoneBenchmarkSimmons: "vs Ben Simmons",
    hrZoneHeadline: "Know Your Gap. Close Your Gap.",
    hrZoneHeadlineDesc:
      "Compare your HR×Zone shooting fingerprint against any baseline — from weekend rec to the greatest shooter who ever lived.",
  },
  comingSoon: {
    badge: "Coming Soon",
    inDevelopment: "In Development",
    backToBasketball: "← Back to Basketball",
  },
  tennis: {
    title: "Tennis",
    subtitle:
      "A counting logger to refine your individual stroke technique. Track serve speed, count rally shots by type (forehand, backhand, volley), and map court zone placement.",
    classicTitle: "Stroke Counter",
    classicDesc:
      "Voice-controlled rally counting by shot type, serve speed logging, court zone placement heatmap, and session history. Offline-first and privacy-focused.",
    aiTitle: "AI Stroke Coach",
    aiDesc:
      "AI stroke classification with form analysis, heart rate monitoring during practice, voice memo transcription for technique notes, and biometric analytics.",
    heroWordmark: "Your Serve. Tracked.",
    heroSubtitle:
      "Voice-controlled stroke counting, T/Body/Wide serve placement analytics, and session history — all on your wrist.",
    voiceTitle: "Count Every Rally.",
    voiceDesc:
      "Say the shot type — forehand, backhand, volley — and ShootCreator logs it instantly. No phone in hand during practice.",
    analyticsTitle: "Court Zone Heatmap",
    analyticsDesc:
      "See where your serves land. T, Body, or Wide — track placement accuracy across Deuce and Ad sides with Hawk-Eye–grade zone analytics.",
    features: [
      { icon: "\u{1F3BE}", title: "Serve Zones", desc: "T / Body / Wide tracking per service box" },
      { icon: "\u{1F399}\u{FE0F}", title: "Voice Counting", desc: "Hands-free shot type logging" },
      { icon: "\u{231A}", title: "Apple Watch", desc: "Count from your wrist mid-rally" },
      { icon: "\u{1F4CA}", title: "Session History", desc: "Review FH/BH/Volley splits over time" },
      { icon: "\u{1F512}", title: "Offline-First", desc: "No account, no cloud, no ads" },
      { icon: "\u{1F30D}", title: "Rally Depth", desc: "Deep / Mid / Short landing zones" },
    ],
    ctaTitle: "Free to Start.",
    ctaButton: "Download on the App Store",
    ctaNote: "Free. No account required.",
  },
  soccer: {
    title: "Soccer",
    subtitle:
      "A shooting practice logger to sharpen your individual shot technique. Track power shots, placement shots, and free kicks with goal zone heatmaps.",
    classicTitle: "Shot Logger",
    classicDesc:
      "Voice-controlled shooting practice counter for power shots, placement shots, and free kicks. Goal zone heatmap, session history. Works offline at any field.",
    aiTitle: "AI Shot Coach",
    aiDesc:
      "AI shot quality analysis with power and placement scoring, heart rate tracking during drills, voice memo transcription, and goal zone biometric dashboards.",
    heroWordmark: "Every Shot. Mapped.",
    heroSubtitle:
      "Voice-controlled shooting practice logger with 6-zone goal analytics, penalty tracking, and session history.",
    voiceTitle: "Log Shots Hands-Free.",
    voiceDesc:
      "Call out the shot type — power, placement, free kick — and the zone. ShootCreator handles the rest while you focus on your technique.",
    analyticsTitle: "6-Zone Goal Analytics",
    analyticsDesc:
      "See your conversion rate by zone — top corners vs low center. Based on the same 2×3 zone model used by StatsBomb and Opta.",
    features: [
      { icon: "\u{26BD}", title: "6-Zone Goal", desc: "Top/Low × L/C/R conversion tracking" },
      { icon: "\u{1F399}\u{FE0F}", title: "Voice Counting", desc: "Hands-free shot type logging" },
      { icon: "\u{231A}", title: "Apple Watch", desc: "Quick-log from the pitch" },
      { icon: "\u{1F3AF}", title: "PK Tracker", desc: "Dedicated penalty kick analytics" },
      { icon: "\u{1F512}", title: "Offline-First", desc: "Works at any field, no wifi needed" },
      { icon: "\u{1F4CA}", title: "Session History", desc: "Track improvement over weeks" },
    ],
    ctaTitle: "Free to Start.",
    ctaButton: "Download on the App Store",
    ctaNote: "Free. No account required.",
  },
  golf: {
    title: "Golf",
    subtitle:
      "A practice logger for approach shots and putting. Track distance control, green-side accuracy, and putt success rates with visual green maps.",
    classicTitle: "Short Game Logger",
    classicDesc:
      "Voice-controlled approach and putt counting by distance zone, green-side accuracy heatmap, session history. Offline-first, no account required.",
    aiTitle: "AI Short Game Coach",
    aiDesc:
      "AI approach quality analysis with distance and line scoring, heart rate tracking during pressure putts, voice memo transcription, and green-side biometric dashboards.",
    heroWordmark: "Dial In Your Wedges.",
    heroSubtitle:
      "Voice-controlled approach and putt logger with Strokes Gained distance bands, putting distance rings, and session analytics.",
    voiceTitle: "Log Without Looking Down.",
    voiceDesc:
      "Call the distance and result — '75 yards, on green' — and keep your rhythm. No phone fumbling between shots.",
    analyticsTitle: "Strokes Gained Zones",
    analyticsDesc:
      "Track GIR% and expected strokes by distance band — from inside 50 yards to 200+. Putting rings show make% at 3ft, 10ft, 20ft, 30ft.",
    features: [
      { icon: "\u{26F3}", title: "Distance Bands", desc: "<50y / 50-100y / 100-150y / 150-200y" },
      {
        icon: "\u{1F399}\u{FE0F}",
        title: "Voice Logging",
        desc: "Call distance and result, hands-free",
      },
      { icon: "\u{231A}", title: "Apple Watch", desc: "Log putts from the green" },
      { icon: "\u{1F4CF}", title: "Putting Rings", desc: "3ft / 10ft / 20ft / 30ft make rates" },
      { icon: "\u{1F512}", title: "Offline-First", desc: "No account, works anywhere" },
      { icon: "\u{1F4CA}", title: "Strokes Gained", desc: "PGA Tour baseline comparison" },
    ],
    ctaTitle: "Free to Start.",
    ctaButton: "Download on the App Store",
    ctaNote: "Free. No account required.",
  },
  aiTeam: {
    heroTitle: "Turn Solo Reps into Team Results.",
    heroSubtitle:
      "Share practice data in real time. Even the reps your coach can't see — the numbers tell the whole story.",
    heroCta: "Create Your Team — Free",
    heroDemo: "Watch Demo",
    painTitle: "Sound Familiar?",
    painCards: [
      { icon: "\u{1F3C0}", role: "Coach", pain: '"I can\'t watch 30 players practice at once."' },
      { icon: "\u{1F4AA}", role: "Player", pain: '"Nobody sees how hard I train on my own."' },
      {
        icon: "\u{1F3EB}",
        role: "Academy",
        pain: '"We can\'t unify data across multiple locations."',
      },
    ],
    painSolution: "ShootCreator AI Teams solves all of this.",
    // Roster
    rosterTitle: "15-Player Roster at a Glance",
    rosterSubtitle:
      "Every player's stats, heart rate, and load status — managed in one unified roster view.",
    rosterLegendOnCourt: "On Court",
    rosterLegendBench: "Bench",
    rosterOnCourtBadge: "ON COURT",
    rosterLoadReady: "Ready",
    rosterLoadMonitor: "Monitor",
    rosterLoadFatigued: "Fatigued",
    rosterPlayers: [
      {
        number: 0,
        name: "S. Fontecchio",
        pos: "SF",
        height: "6'7\"",
        hrAvg: 136,
        loadStatus: "green",
      },
      {
        number: 2,
        name: "T. Rozier",
        pos: "PG",
        height: "6'1\"",
        hrAvg: 152,
        loadStatus: "yellow",
      },
      { number: 5, name: "N. Jovic", pos: "PF", height: "6'10\"", hrAvg: 138, loadStatus: "green" },
      { number: 7, name: "K. Ware", pos: "C", height: "7'0\"", hrAvg: 144, loadStatus: "green" },
      {
        number: 9,
        name: "P. Larsson",
        pos: "SG",
        height: "6'5\"",
        hrAvg: 140,
        loadStatus: "green",
      },
      {
        number: 11,
        name: "J. Jaquez Jr.",
        pos: "SF",
        height: "6'6\"",
        hrAvg: 150,
        loadStatus: "green",
      },
      { number: 12, name: "D. Smith", pos: "PG", height: "6'2\"", hrAvg: 134, loadStatus: "green" },
      {
        number: 13,
        name: "B. Adebayo",
        pos: "C",
        height: "6'9\"",
        hrAvg: 158,
        loadStatus: "yellow",
      },
      { number: 14, name: "T. Herro", pos: "SG", height: "6'5\"", hrAvg: 148, loadStatus: "green" },
      {
        number: 16,
        name: "K. Johnson",
        pos: "SF",
        height: "6'6\"",
        hrAvg: 142,
        loadStatus: "green",
      },
      {
        number: 22,
        name: "A. Wiggins",
        pos: "SF",
        height: "6'7\"",
        hrAvg: 146,
        loadStatus: "green",
      },
      {
        number: 24,
        name: "N. Powell",
        pos: "SG",
        height: "6'4\"",
        hrAvg: 145,
        loadStatus: "green",
      },
      {
        number: 25,
        name: "K. Jakucionis",
        pos: "PG",
        height: "6'5\"",
        hrAvg: 139,
        loadStatus: "green",
      },
      {
        number: 45,
        name: "D. Mitchell",
        pos: "PG",
        height: "6'0\"",
        hrAvg: 143,
        loadStatus: "yellow",
      },
      { number: 50, name: "V. Goldin", pos: "C", height: "7'0\"", hrAvg: 130, loadStatus: "green" },
      {
        number: 99,
        name: "E. Spoelstra",
        pos: "HC",
        height: "6'2\"",
        hrAvg: 205,
        loadStatus: "red",
      },
    ],
    // Realtime Heart Rate
    realtimeTitle: "Real-Time Heart Rate Monitoring",
    realtimeDesc:
      "See every on-court player's live BPM, HR zone, and ECG waveform — updated in real time via Polar sensors.",
    realtimeBleConnected: "Connected",
    realtimeBleReconnecting: "Reconnecting",
    realtimeBleLost: "Lost",
    // Polar BLE
    polarTitle: "Polar BLE Integration",
    polarDesc: "Automatic bench-proximity sync with instant reconnection.",
    polarFeatures: [
      "Bench-proximity auto-connect via BLE",
      "Automatic reconnection on signal loss",
      "Multi-sensor parallel pairing (up to 15)",
      "Real-time data streaming at 1Hz",
    ],
    // Load Management
    loadTitle: "Session-Level Load Management",
    loadDesc: "HRV-driven fatigue tracking — know who's ready and who needs rest.",
    loadMetrics: [
      "HRV (Heart Rate Variability)",
      "TRIMP (Training Impulse)",
      "RPE (Rate of Perceived Exertion)",
    ],
    loadPlayersLabel: "Players",
    loadWeeklyLoadLabel: "Weekly Load",
    loadHrvTrendLabel: "HRV Trend (7d)",
    loadTrimpTrendLabel: "TRIMP Trend (5wk)",
    loadTrainingSessionsLabel: "Training Sessions",
    loadLevelLow: "Low",
    loadLevelMed: "Med",
    loadLevelHigh: "High",
    loadLevelOver: "Over",
    loadLevelRest: "Rest",
    loadDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    loadStatusElevated: "Elevated",
    loadStatusOptimal: "Optimal",
    loadStatusFresh: "Fresh",
    loadStatusRecovery: "Recovery",
    loadStatusOverreaching: "Overreaching",
    loadStatusInjured: "Injured",
    loadSessionTypes: {
      scrimmage5v5: "5v5 Scrimmage",
      shootingDrills: "Shooting Drills",
      gameVsLal: "Game vs LAL",
      lightShoot: "Light Shoot",
      gameVsBkn: "Game vs BKN",
      conditioning: "Conditioning",
      gameVsMia: "Game vs MIA",
      recoveryRun: "Recovery Run",
      teamPractice: "Team Practice",
      gameVsChi: "Game vs CHI",
      shooting: "Shooting",
      filmLight: "Film + Light",
      practice: "Practice",
      rehabWalk: "Rehab Walk",
      lightShooting: "Light Shooting",
      skillsTraining: "Skills Training",
      individualWork: "Individual Work",
      recovery: "Recovery",
      gameVsPhi: "Game vs PHI",
      gameVsAtl: "Game vs ATL",
      lightPractice: "Light Practice",
      poolRehab: "Pool Rehab",
      gameVsBos: "Game vs BOS",
      gameVsNyk: "Game vs NYK",
    },
    // Live Game View
    gameTitle: "Live Game View",
    gameDesc:
      "Real-time court visualization with live heart rates, game stats, and bench substitution tracking.",
    gameLiveScore: "Live Score",
    gameScoreValue: "68 – 61",
    gameBenchLabel: "Bench",
    // Team Calendar
    calendarTitle: "Team Calendar",
    calendarDesc:
      "Games, practices, recovery days — all in one calendar. Weekly team load summaries at a glance.",
    calendarDayHeaders: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    calendarLegendGame: "Game",
    calendarLegendPractice: "Practice",
    calendarLegendRecovery: "Recovery",
    calendarLegendRest: "Rest",
    calendarMonthTitle: "March 2026",
    calendarWeekLoadLabel: "Week Load",
    calendarOpponents: {
      4: "vs LAL",
      7: "vs BKN",
      11: "vs BOS",
      18: "vs CHI",
      21: "vs PHI",
      25: "vs ATL",
    },
    calendarToughWeekLabel: "Tough Week",
    calendarLoadSummary: {
      low: "Low",
      moderate: "Moderate",
      high: "High",
      extreme: "Extreme",
    },
    // Rest Suggestion
    restTitle: "AI Rest Suggestions",
    restDesc:
      "Proactive recovery recommendations based on cumulative load, HRV trends, and upcoming schedule density.",
    restAlertBadge: "ALERT",
    restWarningBadge: "WARNING",
    restInsightBadge: "INSIGHT",
    restSuggestions: [
      {
        type: "alert",
        player: "B. Adebayo",
        text: "TRIMP threshold exceeded 3 consecutive days. Rest day strongly recommended.",
      },
      {
        type: "alert",
        player: "N. Powell",
        text: "ACWR at 1.18 (danger zone). Immediate load reduction required.",
      },
      {
        type: "warning",
        player: "T. Rozier",
        text: "HRV declining trend detected. Monitor workload carefully.",
      },
      {
        type: "warning",
        player: "E. Spoelstra",
        text: "5 consecutive overload days. Schedule mandatory recovery session.",
      },
      {
        type: "insight",
        player: "Team",
        text: "Team 3PT% improved 4.2% this week. Consider increasing perimeter sets.",
      },
      {
        type: "insight",
        player: "T. Herro",
        text: "ACWR optimal at 1.03. Good candidate for increased minutes.",
      },
    ],
    restAiLabel: "AI Recommendation",
    // Management Dashboard
    managementTitle: "Management Dashboard",
    managementDesc: "Four panels, one view — everything a coaching staff needs.",
    managementPanels: [
      { icon: "\u{1F4C8}", title: "FG% Trend", desc: "Team shooting accuracy over time" },
      { icon: "\u{1F6A6}", title: "Load Status", desc: "Per-player readiness at a glance" },
      { icon: "\u{1F4C5}", title: "Weekly Schedule", desc: "This week's games and practices" },
      {
        icon: "\u{1F9E0}",
        title: "AI Insights",
        desc: "Fatigue alerts and performance suggestions",
      },
    ],
    managementLoadReady: "Ready",
    managementLoadMonitor: "Monitor",
    managementLoadRest: "Rest",
    managementSchedule: [
      { icon: "\u{1F4AA}", text: "Team Practice", day: "Mon 9:00" },
      { icon: "\u{1F3C0}", text: "vs Lakers", day: "Tue 19:00" },
      { icon: "\u{1F504}", text: "Recovery Session", day: "Wed 10:00" },
      { icon: "\u{1F4AA}", text: "Shooting Drills", day: "Thu 9:00" },
      { icon: "\u{1F3C0}", text: "vs Nets", day: "Fri 19:30" },
    ],
    managementInsights: [
      {
        type: "warning",
        text: "Adebayo TRIMP exceeded threshold 3 days in a row. Recommend rest day.",
      },
      {
        type: "suggestion",
        text: "Team 3PT% improved 4.2% this week. Consider increasing perimeter sets.",
      },
      { type: "warning", text: "Rozier's HRV trending downward. Monitor workload closely." },
    ],
    managementGaugeLabel: "Team FG%",
    managementGaugeLeft: "Low",
    managementGaugeRight: "High",
    dashboardTitle: "30 Players. One Dashboard.",
    dashboardSubtitle:
      "See every player's growth curve, practice frequency, and zone accuracy — at a glance.",
    modelsTitle: "Three Ways to Share",
    models: [
      {
        id: "personal",
        icon: "\u{1F91D}",
        title: "Personal Sharing",
        desc: "Share your data with a trusted coach or mentor. You control what to share and when to revoke.",
      },
      {
        id: "team",
        icon: "\u{1F465}",
        title: "Team Mode",
        desc: "Manage an entire club, academy, or school team. Coaches see all players in one dashboard.",
      },
      {
        id: "community",
        icon: "\u{1F310}",
        title: "Community",
        desc: "Skill coaches build their own community. Distribute drills, track progress, grow your brand.",
      },
    ],
    featuresTitle: "Built for Coaches and Players",
    features: [
      {
        icon: "\u{1F4CA}",
        title: "Team Dashboard",
        desc: "FG% trends, practice heatmap, zone analytics — all in one view.",
      },
      {
        icon: "\u{1F4F1}",
        title: "Smart Invite",
        desc: "QR code or link. One scan to join the team.",
      },
      {
        icon: "\u{1F4AC}",
        title: "Coach Feedback",
        desc: "Comment on sessions with AI-assisted insights.",
      },
      {
        icon: "\u{1F3C6}",
        title: "Leaderboard",
        desc: "Healthy competition drives growth. Zone accuracy rankings.",
      },
      {
        icon: "\u{1F4CB}",
        title: "Drill Plans",
        desc: "Distribute practice menus. Track completion automatically.",
      },
      {
        icon: "\u{1F512}",
        title: "Role Management",
        desc: "Owner, Coach, Player, Viewer — right access for everyone.",
      },
    ],
    pricingTitle: "Plans for Every Team Size",
    plans: [
      {
        name: "Free",
        price: "$0",
        note: "Forever",
        members: "Up to 5 members",
        features: ["1 team", "Basic dashboard", "Leaderboard"],
        cta: "Start Free",
      },
      {
        name: "Pro",
        price: "$39",
        note: "/month",
        members: "Up to 25 members",
        features: [
          "3 teams",
          "Full dashboard",
          "Coach feedback",
          "Drill plans",
          "All leaderboards",
        ],
        cta: "Start 30-Day Trial",
        recommended: true,
      },
      {
        name: "Enterprise",
        price: "$149",
        note: "/month",
        members: "Unlimited members",
        features: [
          "Unlimited teams",
          "Custom branding",
          "API access",
          "AI insights",
          "Priority support",
        ],
        cta: "Contact Sales",
      },
    ],
    ctaTitle: "Unlock Your Team's Potential with Data.",
    ctaSubtitle: "Pro plan — 30-day free trial. No credit card required.",
    ctaButton: "Create Your Team",
  },
  footer: {
    basketballClassic: "Basketball Classic",
    basketballAi: "Basketball AI",
    tennis: "Tennis",
    soccer: "Soccer",
    golf: "Golf",
    github: "GitHub",
    copyright: `© ${new Date().getFullYear()} ShootCreator. All rights reserved.`,
  },
  common: {
    scroll: "Scroll",
  },
};
